<template>
  <div class="page">
    <div class="sub-header">
      <div class="left">
        <span class="label">
          <a-range-picker
            v-model:value="createTimeRange"
            :allowClear="false"
            show-time
            @change="createTimeRangeChange"
            valueFormat="YYYY-MM-DD HH:mm"
          />
        </span>
      </div>
      <div class="right">
        <a-button type="link" @click="minVisible = true">
          <template #icon>
            <container-outlined />
          </template>
          Prod Line
        </a-button>
        <a-divider type="vertical"></a-divider>
        <a-button type="link" @click="onExport">
          <template #icon>
            <download-outlined />
          </template>
          Export
        </a-button>
      </div>
    </div>
    <a-table
      :columns="columns"
      :row-key="(record) => record.id"
      :data-source="dataSource"
      :loading="loading"
      size="small"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.dataIndex === 'stopWS'">
          <WarningFilled
            v-if="record.stopWS"
            style="
               {
                font-size: 22px;
                color: red;
              }
            "
          />
          <span v-else>-</span>
        </template>
      </template>
    </a-table>
  </div>
  <a-modal
    v-model:visible="minVisible"
    :width="450"
    title="Production Line Information"
    wrap-class-name="has-table-full-modal"
  >
    <template #footer>
      <a-button type="primary" @click="minVisible = false">Close</a-button>
    </template>
    <a-table
      :columns="minColumns"
      :data-source="summaryNetList"
      bordered
      :pagination="false"
      size="small"
    ></a-table>
  </a-modal>
</template>

<script>
  import {
    WarningFilled,
    ContainerOutlined,
    DownloadOutlined,
  } from '@ant-design/icons-vue'
  import { mapGetters } from 'vuex'
  import { parseTime } from '@/utils'
  import dayjs from 'dayjs'
  import { getAndonSummary, getAndonSummaryRow } from '@/api/andon-overview'
  export default {
    components: { WarningFilled, ContainerOutlined, DownloadOutlined },
    props: {
      pageParams: {
        type: Object,
        default() {
          return {
            id: 0,
            uiid: 'Andonoverview01',
          }
        },
      },
      isOpen: {
        type: Boolean,
        default: false,
      },
    },
    computed: {
      ...mapGetters({
        currentSystem: 'user/currentSystem',
      }),
    },
    data() {
      const start = dayjs().subtract(7, 'day').format('YYYY-MM-DD HH:mm')
      const end = dayjs().format('YYYY-MM-DD HH:mm')
      return {
        minVisible: false,
        loading: false,
        createTimeRange: [start, end],
        dataSource: [],
        summaryNetList: [],
        columns: [
          {
            title: 'No',
            dataIndex: 'no',
            key: 'no',
            customRender({ index }) {
              return index + 1
            },
            width: 60,
          },
          {
            title: 'Prd Line',
            dataIndex: 'net_id',
            minWidth: 100,
            sorter: (a, b) => a.net_id.charCodeAt() - b.net_id.charCodeAt(),
          },
          {
            title: 'Job ID',
            dataIndex: 'job_id',
            minWidth: 100,
          },
          {
            title: 'Description',
            dataIndex: 'andonDesc',
            minWidth: 100,
          },
          {
            title: 'Create Time',
            dataIndex: 'create_on',
            minWidth: 100,
            defaultSortOrder: 'descend',
            customRender({ text }) {
              return parseTime(text, '{m}/{d} {h}:{i}')
            },
            sorter: (a, b) =>
              dayjs(a.create_on).valueOf() - dayjs(b.create_on).valueOf(),
          },
          {
            title: 'StopWS',
            dataIndex: 'stop_WS',
            minWidth: 100,
          },
          {
            title: 'Stop WS',
            dataIndex: 'stop_WS',
            minWidth: 100,
          },
          {
            title: 'Lost Hr',
            dataIndex: 'lostHr',
            minWidth: 100,
            sorter: (a, b) => a.lostHr.charCodeAt() - b.lostHr.charCodeAt(),
          },
          {
            title: 'Status',
            dataIndex: 'status',
            minWidth: 100,
            sorter: (a, b) => a.status.charCodeAt() - b.status.charCodeAt(),
          },
          {
            title: 'Resp(Min)',
            dataIndex: 'resp',
            minWidth: 100,
            sorter: (a, b) => a.resp - b.resp,
          },
          {
            title: 'Close(Hr)',
            dataIndex: 'closeHr',
            minWidth: 100,
            sorter: (a, b) => a.closeHr - b.closeHr,
          },
        ],
        minColumns: [
          {
            title: 'Production Line',
            dataIndex: 'netGrpId',
            minWidth: 100,
          },
          {
            title: 'Total Andon',
            dataIndex: 'totalAndon',
            minWidth: 100,
          },
          {
            title: 'Lost Hour',
            dataIndex: 'lostHour',
            minWidth: 100,
          },
        ],
      }
    },
    watch: {
      isOpen: {
        handler(v) {
          if (v) {
            this._getAndonSummaryData()
          }
        },
        immediate: true,
      },
    },
    methods: {
      createTimeRangeChange() {
        this._getAndonSummaryData()
      },
      async refresh() {
        await this._getAndonSummaryData()
        return Promise.resolve()
      },
      async _getAndonSummaryData() {
        const { id, uiid, row } = this.pageParams
        const start = dayjs(this.createTimeRange[0]).format(
          'YYYY-MM-DD HH:mm:ss'
        )
        const end = dayjs(this.createTimeRange[1]).format('YYYY-MM-DD HH:mm:ss')
        const query = {
          id,
          uiid,
          start,
          end,
        }
        this.loading = true
        try {
          if (!row) {
            const { body } = await getAndonSummary(query)
            this.dataSource = body.andons
            this.summaryNetList = body.items
          } else {
            const { body } = await getAndonSummaryRow({ ...query, row })
            this.summaryNetList = body.items
            this.dataSource = body.andons
          }
          this.loading = false
        } catch (e) {
          this.loading = false
        }

        return Promise.resolve()
      },
      onExport() {
        let url = ''
        const { id, uiid, row } = this.pageParams
        const start = dayjs(this.createTimeRange[0]).valueOf()
        const end = dayjs(this.createTimeRange[1]).valueOf()
        if (this.pageParams.row) {
          url = `/api/andon/overview/summary/row/download?current-system=${this.currentSystem}&id=${id}&uiid=${uiid}&start=${start}&end=${end}&row=${row}`
        } else {
          url = `/api/andon/overview/summary/download?current-system=${this.currentSystem}&id=${id}&uiid=${uiid}&start=${start}&end=${end}`
        }
        window.open(url, '_blank')
      },
    },
  }
</script>

<style lang="less" scoped>
  .sub-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 5px 10px;
  }
</style>
